/**
 * 회사 자가 생성 + 회사 전환 훅 (다회사).
 *
 * - useCreateCompanySelf: POST /companies/self — 현재 사용자를 새 회사 CompanyAdmin 으로 추가.
 * - useSwitchCompany: 활성 회사 전환(헤더 갱신 + 전체 쿼리 무효화 + 워크스페이스 재선택).
 */
import { useMutation } from "@tanstack/react-query";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { apiFetch, setActiveCompany } from "@/lib/api-client";
import { useCompanyStore } from "@/store/company-store";

/** POST /companies/self 요청. */
export interface SelfCompanyCreateRequest {
  companyName: string;
  plan: string;
  preset: string;
  workspaceName: string;
}

/** 회사 생성/전환 응답(=SignupResponse 계약). */
export interface CompanyCreateResponse {
  userId: string;
  companyId: string;
  workspaceId: string;
  workspaceSlug: string;
  email: string | null;
  name: string;
}

/** 회사 자가 생성(인증) — 현재 사용자가 새 회사의 CompanyAdmin 이 됨. */
export function useCreateCompanySelf() {
  return useMutation({
    mutationFn: (body: SelfCompanyCreateRequest) =>
      apiFetch<CompanyCreateResponse>("/api/companies/self", { method: "POST", body }),
  });
}

/**
 * 활성 회사 전환 콜백. 헤더(X-Company-Id) 갱신 + 스토어 갱신 + 전체 쿼리 무효화(회사가 바뀌면 모든 데이터 재조회) 후
 * 워크스페이스 선택 화면으로 이동(회사마다 워크스페이스가 다름).
 */
export function useSwitchCompany() {
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);
  const navigate = useNavigate();

  return useCallback(
    (companyId: string, workspaceSlug?: string) => {
      setActiveCompany(companyId); // api-client 헤더(단일 소유)
      setActiveCompanyId(companyId); // UI 스토어
      // 회사 전환은 항상 리졸버(/workspaces, AppShell 밖)를 경유함.
      //   AppShell 이 언마운트되며 옛 워크스페이스 스코프 쿼리들이 함께 사라지므로, 새 회사 컨텍스트로 그 옛 슬러그를
      //   재조회하다가 404 나는 문제를 원천 차단함. 전체 쿼리 무효화(새 회사 데이터 갱신)는 리졸버 마운트 시 수행.
      //   타깃 워크스페이스를 알면 ?to 로 전달해 랜딩 계산 없이 그 WS 로 직행함.
      const to = workspaceSlug
        ? `/workspaces?to=${encodeURIComponent(workspaceSlug)}`
        : "/workspaces";
      navigate(to, { replace: true });
    },
    [setActiveCompanyId, navigate],
  );
}
